// 类型定义
type QRgb = number; // 兼容C++的QRgb，用数字存储RGB值（0xRRGGBB）
type MapMode = 0 | 1 | 2; // NewColor=0, Auto=1, SourceImage=2

interface ImageChannelStatics {
  min: number;
  max: number;
  avg: number;
  stddev: number;
}

interface ImageStatics {
  hStat: ImageChannelStatics;
  sStat: ImageChannelStatics;
  vStat: ImageChannelStatics;
  h: number;
  s: number;
  v: number;
  pixelCount: number;
  saturationMode: MapMode;
}

interface ChangeColorMaskInfo {
  colorTable: ImageStatics[];
  autoCreated: boolean;
  imageWidth: number;
  imageHeight: number;
}

interface HSV {
  h: number;
  s: number;
  v: number;
}

// 常量定义
const NUM_DIMENSIONS = 3;
const kMinSaturationThreshold = 40;
const kMinValueThreshold = 25;
const kMaxSaturation = 255;
const kMaxValue = 255;
const kDistanctTheshold = 86.6;

// 工具函数：QRgb相关操作（兼容C++的qHue/qSat/qVal）
const qHue = (hsv: QRgb): number => hsv & 0xff;
const qSat = (hsv: QRgb): number => (hsv >> 8) & 0xff;
const qVal = (hsv: QRgb): number => (hsv >> 16) & 0xff;

// 工具函数：RGB转HSV（OpenCV风格，h范围0-180, s/v 0-255）
const rgb2hsv = (r: number, g: number, b: number): HSV => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, v = max;

  const d = max - min;
  s = max === 0 ? 0 : d / max;

  if (max !== min) {
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h *= 30; // OpenCV的H范围是0-180（不是0-360）
  }
  return {
    h: Math.round(h),
    s: Math.round(s * 255),
    v: Math.round(v * 255)
  };
};

// 工具函数：HSV转RGB（OpenCV风格）
const hsv2rgb = (h: number, s: number, v: number): { r: number, g: number, b: number } => {
  h /= 30; // 转回0-6的范围
  s /= 255;
  v /= 255;

  const i = Math.floor(h);
  const f = h - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  const mod = i % 6;

  let r = 0, g = 0, b = 0;
  switch (mod) {
    case 0: r = v; g = t; b = p; break;
    case 1: r = q; g = v; b = p; break;
    case 2: r = p; g = v; b = t; break;
    case 3: r = p; g = q; b = v; break;
    case 4: r = t; g = p; b = v; break;
    case 5: r = v; g = p; b = q; break;
  }
  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  };
};

// 工具函数：加载JSON（替代C++的LoadJsonFromFileByStream）
const loadJsonFromUrl = async (url: string): Promise<any> => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`加载JSON失败: ${url}`);
  return await response.json();
};

// JSON反序列化（替代C++的fromJson）
const fromJson = (json: any): ChangeColorMaskInfo => {
  const maskInfo: ChangeColorMaskInfo = {
    colorTable: [],
    autoCreated: json.autoCreated || false,
    imageWidth: json.imageWidth || 0,
    imageHeight: json.imageHeight || 0
  };

  if (json.imageStatics && Array.isArray(json.imageStatics)) {
    json.imageStatics.forEach((item: any) => {
      const is: ImageStatics = {
        h: item.h || 0,
        s: item.s || 0,
        v: item.v || 0,
        pixelCount: item.pixelCount || 0,
        saturationMode: (item.saturationMode as MapMode) || 1,
        hStat: {
          min: item.hStat?.min || 0,
          max: item.hStat?.max || 0,
          avg: item.hStat?.avg || 0,
          stddev: item.hStat?.stddev || 0
        },
        sStat: {
          min: item.sStat?.min || 0,
          max: item.sStat?.max || 0,
          avg: item.sStat?.avg || 0,
          stddev: item.sStat?.stddev || 0
        },
        vStat: {
          min: item.vStat?.min || 0,
          max: item.vStat?.max || 0,
          avg: item.vStat?.avg || 0,
          stddev: item.vStat?.stddev || 0
        }
      };
      maskInfo.colorTable.push(is);
    });
  }
  return maskInfo;
};

// 加载蒙版信息（替代C++的loadFromJsonFile）
const loadMaskInfoFromUrl = async (url: string): Promise<ChangeColorMaskInfo> => {
  const json = await loadJsonFromUrl(url);
  return fromJson(json);
};

// 加载图片（URL→HTMLImageElement）
const loadImageFromUrl = async (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous"; // 跨域处理
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`加载图片失败: ${url}`));
    img.src = url;
  });
};

// 加载多个蒙版图片并转为灰度图（替代C++的OpenMultipleMaskFilesToQImageVector）
const loadMultipleMaskImages = async (urls: string[]): Promise<HTMLImageElement[]> => {
  if (urls.length === 0) return [];

  const maskImages: HTMLImageElement[] = [];
  const firstImg = await loadImageFromUrl(urls[0] || "");
  
  // 统一转为灰度图（模拟QImage::Format_Indexed8）
  const firstGray = convertToGrayscale(firstImg);
  maskImages.push(firstGray);

  for (let i = 1; i < urls.length; i++) {
    const img = await loadImageFromUrl(urls[i] || "");
    if (img.width !== firstImg.width || img.height !== firstImg.height) {
      throw new Error(`蒙版尺寸不匹配: ${urls[i]}`);
    }
    const grayImg = convertToGrayscale(img);
    maskImages.push(grayImg);
  }

  return maskImages;
};

// 图片转灰度图（模拟QImage的Indexed8格式）
const convertToGrayscale = (img: HTMLImageElement): HTMLImageElement => {
  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext("2d")!;
  
  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  // 转灰度：Y = 0.299R + 0.587G + 0.114B
  for (let i = 0; i < data.length; i += 4) {
    const red = data[i] ?? 255;
    const green = data[i + 1] ?? 255;
    const blue = data[i + 1] ?? 255;
    const gray = Math.round(0.299 * red + 0.587 * green + 0.114 * blue);
    data[i] = gray;
    data[i + 1] = gray;
    data[i + 2] = gray;
    data[i + 3] = 255; // 不透明
  }

  ctx.putImageData(imageData, 0, 0);
  const grayImg = new Image();
  grayImg.src = canvas.toDataURL();
  return grayImg;
};

function logTen(v:number){
    return Math.log(v) / Math.log(10);
}

// 变色算子类（替代C++的ChangeHsvItem）
class ChangeHsvItem {
  private hsvOld: HSV;
  private hsvNew: HSV;
  private hStat: ImageChannelStatics;
  private sStat: ImageChannelStatics;
  private vStat: ImageChannelStatics;
  private sMode: MapMode;
  private minNewHue: number;
  private maxNewHue: number;
  private gamma: number;
  private theta: number;

  constructor(
    oldHsv: HSV,
    newHsv: HSV,
    h: ImageChannelStatics,
    s: ImageChannelStatics,
    v: ImageChannelStatics,
    saturationMode: MapMode
  ) {
    this.hsvOld = oldHsv;
    this.hsvNew = newHsv;
    this.hStat = h;
    this.sStat = s;
    this.vStat = v;
    this.sMode = saturationMode;

    // 计算Hue范围
    this.minNewHue = Math.round(newHsv.h - 1 * h.stddev);
    this.maxNewHue = Math.round(newHsv.h + 1 * h.stddev);

    // 计算Gamma和Theta

    this.gamma = logTen(newHsv.v / 255.0) / logTen(oldHsv.v / 255.0);
    if (this.gamma < 0.7) {
      this.gamma = 0.7;
    } else if (this.gamma > 2.0) {
      this.gamma = 2.0;
    }
    this.theta = (newHsv.v / 255.0) - Math.pow(oldHsv.v / 255.0, this.gamma);
  }

  h(): number {
    return this.hsvNew.h;
  }

  s(): number {
    return this.hsvNew.s;
  }

  v(): number {
    return this.hsvNew.v;
  }

  map_h(inputH: number): number {
    if (this.sMode === 0) return this.hsvNew.h; // NewColor
    if (this.hsvNew.s < kMinSaturationThreshold || this.hsvNew.v < kMinValueThreshold) {
      return this.hsvNew.h;
    }
    if (this.hsvOld.s < kMinSaturationThreshold || this.hsvOld.v < kMinValueThreshold) {
      return this.hsvNew.h;
    }

    const dh = inputH - this.hStat.avg;
    let h = this.hsvNew.h + dh;

    if (h < this.minNewHue) h = this.minNewHue;
    else if (h > this.maxNewHue) h = this.maxNewHue;

    if (h < 0) h += 180;
    else if (h > 180) h -= 180;

    return Math.round(h);
  }

  map_s(inputS: number): number {
    if (this.sMode === 0) return this.hsvNew.s; // NewColor
    if (this.sMode === 1) { // Auto
      if (this.hsvNew.s < kMinSaturationThreshold || this.hsvNew.v < kMinValueThreshold) {
        return this.hsvNew.s;
      }
      if (this.hsvOld.s < kMinSaturationThreshold || this.hsvOld.v < kMinValueThreshold) {
        return this.hsvNew.s;
      }
    }
    // SourceImage模式：使用原图饱和度偏移
    const ds = this.hsvNew.s - this.hsvOld.s;
    const s = inputS + ds;
    return Math.max(0, Math.min(kMaxSaturation, Math.round(s)));
  }

  map_v(inputV: number): number {
    // Gamma调节亮度
    const vNorm = inputV / 255.0;
    const vNew = (Math.pow(vNorm, this.gamma) + this.theta) * 255.0;
    return Math.max(0, Math.min(kMaxValue, Math.round(vNew)));
  }
}

// 核心：蒙版变色处理（基于Canvas）
const changeColorWithMasks = async (
  baseImage: HTMLImageElement,
  masks: HTMLImageElement[],
  srcColorTable: ImageStatics[],
  dstColorTableRGB: QRgb[]
): Promise<HTMLCanvasElement> => {
  // 1. 初始化Canvas
  const canvas = document.createElement("canvas");
  canvas.width = baseImage.width;
  canvas.height = baseImage.height;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(baseImage, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  // 2. RGB转HSV（逐像素）
  const hsvData: HSV[] = [];
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i] ?? 255;
    const g = data[i + 1] ?? 255;
    const b = data[i + 2] ?? 255;
    hsvData.push(rgb2hsv(r, g, b));
  }

  // 3. 处理目标颜色表（RGB→HSV）
  const dstColorTableHSV: HSV[] = [];
  dstColorTableRGB.forEach(rgb => {
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >> 8) & 0xff;
    const b = rgb & 0xff;
    dstColorTableHSV.push(rgb2hsv(r, g, b));
  });

  // 4. 补全颜色表（如果目标颜色数量不足）
  if (dstColorTableHSV.length < srcColorTable.length) {
    for (let i = dstColorTableHSV.length; i < srcColorTable.length; i++) {
      dstColorTableHSV.push({
        h: srcColorTable[i]?.h ?? 0,
        s: srcColorTable[i]?.s ?? 0,
        v: srcColorTable[i]?.v ?? 0
      });
    }
  }

  //// 5. 逐蒙版处理变色
  //const outputBGRData: number[][] = Array.from({ length: masks.length }, () => []);
  // 逐蒙版处理变色（ES5兼容写法）
  const outputBGRData: number[][] = [];
  for (let i = 0; i < masks.length; i++) {
    outputBGRData.push([]); // 为每个蒙版初始化一个空数组
  }

  for (let cluster = 0; cluster < masks.length; cluster++) {
    const mask :HTMLImageElement = masks[cluster]!;
    const maskCanvas = document.createElement("canvas");
    maskCanvas.width = mask.width;
    maskCanvas.height = mask.height;
    const maskCtx = maskCanvas.getContext("2d")!;
    maskCtx.drawImage(mask, 0, 0);
    const maskData = maskCtx.getImageData(0, 0, mask.width, mask.height).data;

    const src:ImageStatics = srcColorTable[cluster]!;
    const dst:HSV = dstColorTableHSV[cluster]!;
    const cci = new ChangeHsvItem(
      { h: src.h, s: src.s, v: src.v },
      dst,
      src.hStat,
      src.sStat,
      src.vStat,
      src.saturationMode
    );

    // 逐像素应用蒙版和变色规则
    for (let i = 0; i < hsvData.length; i++) {
      const maskPixel = maskData[i * 4]; // 灰度值（0=透明，>0=蒙版区域）
      if (maskPixel === 0) {
        outputBGRData[cluster]![i * 3] = data[i * 4]!;     // R
        outputBGRData[cluster]![i * 3 + 1] = data[i * 4 + 1]!; // G
        outputBGRData[cluster]![i * 3 + 2] = data[i * 4 + 2]!; // B
        continue;
      }

      // 映射HSV
      const h = cci.map_h(hsvData[i]!.h);
      const s = cci.map_s(hsvData[i]!.s);
      const v = cci.map_v(hsvData[i]!.v);

      // HSV转回RGB
      const rgb = hsv2rgb(h, s, v);
      outputBGRData[cluster]![i * 3] = rgb.r;     // R
      outputBGRData[cluster]![i * 3 + 1] = rgb.g; // G
      outputBGRData[cluster]![i * 3 + 2] = rgb.b; // B
    }
  }

  // 6. 合并多蒙版结果（优化：预先缓存所有mask的ImageData）
  const maskDataCache: Uint8ClampedArray[] = [];
  for (let cluster = 0; cluster < masks.length; cluster++) {
    const maskCanvas = document.createElement("canvas");
    maskCanvas.width = masks[cluster]!.width;
    maskCanvas.height = masks[cluster]!.height;
    const maskCtx = maskCanvas.getContext("2d")!;
    maskCtx.drawImage(masks[cluster]!, 0, 0);
    maskDataCache.push(maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height).data);
  }

  // 逐像素合并
  for (let i = 0; i < data.length; i += 4) {
    const pixelIdx = i / 4;
    let sumMask = 0;
    let b = 0, g = 0, r = 0;

    // 计算蒙版权重总和
    for (let cluster = 0; cluster < masks.length; cluster++) {
      const maskVal = maskDataCache[cluster]![i]!;
      if (maskVal > 0) sumMask += maskVal;
    }

    // 加权合并颜色
    if (sumMask > 0) {
      for (let cluster = 0; cluster < masks.length; cluster++) {
        const maskVal = maskDataCache[cluster]![i]!;
        
        if (maskVal > 0) {
          const rVal = outputBGRData[cluster]![pixelIdx * 3]!;
          const gVal = outputBGRData[cluster]![pixelIdx * 3 + 1]!;
          const bVal = outputBGRData[cluster]![pixelIdx * 3 + 2]!;
          
          r += (rVal * maskVal) / sumMask;
          g += (gVal * maskVal) / sumMask;
          b += (bVal * maskVal) / sumMask;
        }
      }
    } else {
      // 无蒙版区域保留原图颜色
      r = data[i]!;
      g = data[i + 1]!;
      b = data[i + 2]!;
    }

    // 写入最终颜色
    data[i] = Math.min(255, Math.round(r));     // R
    data[i + 1] = Math.min(255, Math.round(g)); // G
    data[i + 2] = Math.min(255, Math.round(b)); // B
    data[i + 3] = 255; // 透明度（Alpha）
  }

  // 7. 绘制最终结果
  ctx.putImageData(imageData, 0, 0);
  return canvas;
};

/**
 * 将颜色字符串（如#FF0000、#F00、FF0000）转换为QRgb数字（0xRRGGBB）
 * @param colorStr 颜色字符串，支持#FF0000、#F00、FF0000格式
 * @returns QRgb数字（0xRRGGBB），解析失败返回0（黑色）
 */
function colorStrToQRgb(colorStr: string): QRgb {
  // 1. 去除#号，统一转为大写，清洗无效字符
  const cleanStr = colorStr.replace(/^#/, '').toUpperCase().replace(/[^0-9A-F]/g, '');
  
  // 2. 处理短格式（如#F00 → #FF0000）
  let rgbStr = cleanStr;
  if (rgbStr.length === 3) {
    rgbStr = rgbStr.split('').map(char => char + char).join('');
  }
  
  // 3. 验证长度并转换为十六进制数字（0xRRGGBB）
  if (rgbStr.length !== 6) {
    console.warn(`无效的颜色字符串: ${colorStr}，返回默认黑色`);
    return 0x000000; // 解析失败返回黑色
  }
  
  // 4. 转换为数字（parseInt第二个参数传16表示十六进制）
  return parseInt(rgbStr, 16);
}

// 主函数
export const getImageColorChangedByMask = async (
  baseImageUrl: string,
  maskImageUrls: string[],
  maskInfoJsonUrl: string,
  colors: string[],
  outputSize?: { width: number, height: number } // 可选的输出尺寸
): Promise<HTMLCanvasElement | null> => {
  try {
    // 1. 加载蒙版信息JSON
    const maskInfo = await loadMaskInfoFromUrl(maskInfoJsonUrl);
    if (maskInfo.colorTable.length === 0) return null;

    console.log('[getImageColorChangedByMask] mask_info.json 信息:', {
      imageWidth: maskInfo.imageWidth,
      imageHeight: maskInfo.imageHeight,
      colorTableLength: maskInfo.colorTable.length
    });

    // 2. 加载蒙版图片
    const masks = await loadMultipleMaskImages(maskImageUrls);
    if (masks.length === 0) return null;

    // 3. 加载基础图片
    const baseImage = await loadImageFromUrl(baseImageUrl);
    if (!baseImage) return null;

    console.log('[getImageColorChangedByMask] 实际加载的图片尺寸:', {
      baseImage: { width: baseImage.width, height: baseImage.height, url: baseImageUrl },
      mask0: { width: masks[0]!.width, height: masks[0]!.height, url: maskImageUrls[0] },
      maskInfoSize: { width: maskInfo.imageWidth, height: maskInfo.imageHeight },
      needResize: baseImage.width !== masks[0]!.width || baseImage.height !== masks[0]!.height,
      outputSize
    });

    // 4. 适配图片尺寸 - 如果尺寸不一致，以 mask 的尺寸为准
    if (baseImage.width !== masks[0]!.width || baseImage.height !== masks[0]!.height) {
      console.warn('[getImageColorChangedByMask] 图片尺寸不一致，进行缩放适配');
      const scaledCanvas = document.createElement("canvas");
      scaledCanvas.width = masks[0]!.width;
      scaledCanvas.height = masks[0]!.height;
      const ctx = scaledCanvas.getContext("2d")!;
      ctx.drawImage(baseImage, 0, 0, scaledCanvas.width, scaledCanvas.height);
      const scaledImg = new Image();
      scaledImg.src = scaledCanvas.toDataURL();
      await new Promise(resolve => scaledImg.onload = resolve);
      baseImage.src = scaledImg.src;
      
      console.log('[getImageColorChangedByMask] 缩放后尺寸:', {
        width: baseImage.width,
        height: baseImage.height
      });
    }

    // 5. 执行变色处理
    let rgbColors:QRgb[] = [];
    for (let i = 0; i < colors.length; ++i){
        rgbColors.push(colorStrToQRgb(colors[i]!));
    }
    let resultCanvas = await changeColorWithMasks(baseImage, masks, maskInfo.colorTable, rgbColors);
    
    console.log('[getImageColorChangedByMask] 变色完成，输出尺寸:', {
      width: resultCanvas.width,
      height: resultCanvas.height
    });
    
    // 6. 如果指定了输出尺寸，进行缩放
    if (outputSize && (resultCanvas.width !== outputSize.width || resultCanvas.height !== outputSize.height)) {
      console.log('[getImageColorChangedByMask] 缩放到目标尺寸:', outputSize);
      const scaledCanvas = document.createElement("canvas");
      scaledCanvas.width = outputSize.width;
      scaledCanvas.height = outputSize.height;
      const ctx = scaledCanvas.getContext("2d")!;
      ctx.drawImage(resultCanvas, 0, 0, outputSize.width, outputSize.height);
      resultCanvas = scaledCanvas;
    }
    
    return resultCanvas;
  } catch (error) {
    console.error("变色处理失败:", error);
    return null;
  }
};